// @flow
import type {V2SessionDocumentation} from 'api/v2';
import type {UISessionDocumentType} from '../model/types';
import {orgServiceTypesQueryOptions} from './useOrgServiceTypes';
import {toUIServiceTypes, toUISessionDocument, memberToBasic} from './adapter';

// V2 returns member/staff/typeOfService as bare UUIDs, so resolve them to the
// nested UI shape the documentation tree consumes. Members are fetched through
// memberStore (which caches + dedupes across pages); service-type names come
// from the org-service-types query (cached via queryClient). Used by both the
// list query and the single-document flows so resolution stays in one place.
export const resolveDocuments = async (
  apiDocs: Array<V2SessionDocumentation>,
  {
    memberStore,
    queryClient,
  }: {
    memberStore: Object,
    queryClient: Object,
  },
): Promise<Array<UISessionDocumentType>> => {
  const serviceTypesResponse = await queryClient.fetchQuery(
    orgServiceTypesQueryOptions(),
  );
  const serviceTypes = toUIServiceTypes(serviceTypesResponse.data.results);

  // member/staff are Member PKs; there's no bulk-by-id endpoint, so fetch each
  // unique id in parallel — memberStore caches, so repeats/pages are cheap.
  const memberIds = [
    ...new Set(
      apiDocs.flatMap((doc) => [doc.member, doc.staff]).filter(Boolean),
    ),
  ];
  await Promise.all(memberIds.map((id) => memberStore.getMember({id})));

  return apiDocs.map((apiDoc) =>
    toUISessionDocument(apiDoc, {
      member: memberToBasic(memberStore.findMember(apiDoc.member)),
      staff: memberToBasic(memberStore.findMember(apiDoc.staff)),
      serviceTypes,
    }),
  );
};
